import _ from 'lodash';
import { FormItemProps } from 'antd';
import { RuleObject, RuleRender } from 'antd/lib/form';
import validators from './formValidators';
import { DateTime } from 'luxon';
import {
    PHONE_CELLPHONE_REGEX,
    CELLPHONE_REGEX,
    UUID_REGEX,
    ADDRESS_REGEX,
} from './strings';
import { voidFn } from './utilityTypes';

export type Rule = Required<FormItemProps>['rules'][number];

const isRuleObjectArray = (
    rules: (RuleObject | RuleRender)[]
): rules is RuleObject[] => {
    return rules.every(rule => typeof rule !== 'function');
};

// Not all ANT rules are supported. Implement if needed.
// eslint-disable-next-line
const sequentialRules = (rules: (RuleObject | RuleRender)[]): Rule => {
    if (!isRuleObjectArray(rules)) {
        throw new Error('Sequential rules not implemented for RuleRender yet.');
    }

    // eslint-disable-next-line
    const pattern = (rule: RuleObject, input: any): boolean => {
        if (rule.pattern) {
            const regexTest = RegExp(rule.pattern.source).test(input);
            if (!regexTest) return false;
        }
        return true;
    };

    // eslint-disable-next-line
    const len = (rule: RuleObject, input: any): boolean => {
        if (rule.len) return input?.length === rule.len;
        return true;
    };

    // eslint-disable-next-line
    const max = (rule: RuleObject, input: any): boolean => {
        if (rule.max) return input?.length <= rule.max;
        return true;
    };

    // eslint-disable-next-line
    const min = (rule: RuleObject, input: any): boolean => {
        if (rule.min) return input?.length >= rule.min;
        return true;
    };

    // eslint-disable-next-line
    const required = (rule: RuleObject, input: any): boolean => {
        const empty = [undefined, null, '', []];
        if (rule.required) return !empty.includes(input);
        return true;
    };

    const validators = [pattern, len, max, min, required];

    return {
        // eslint-disable-next-line
        validator: async (ruleObj: RuleObject, input: unknown) => {
            for (const rule of rules) {
                const transformedInput = rule.transform
                    ? rule.transform(input)
                    : input;

                for (const validator of validators) {
                    if (!validator(rule, transformedInput))
                        return Promise.reject(rule.message);
                }

                try {
                    await rule?.validator?.(ruleObj, transformedInput, voidFn);
                } catch (e) {
                    if (e) {
                        if (typeof e === 'string') return Promise.reject(e);
                        return e;
                    }
                    return Promise.reject(rule.message);
                }
            }
            return Promise.resolve();
        },
    };
};

const trim = (value?: string) => value && value.trim();

const extractNumber = (value?: string): string | undefined =>
    value && value.replace(/[^0-9]/g, '');

const definedRules = (rules: Array<Rule | undefined | null>): Rule[] =>
    rules.filter((x): x is Rule => Boolean(x));

const required = (message = 'Este campo é obrigatório'): Rule => ({
    required: true,
    message,
});

const email = (blockPlusSign = false, message = 'E-mail inválido'): Rule => ({
    validator: validators.email(blockPlusSign)(message),
    transform: trim,
});

const nickname = (
    message = 'Um nome válido é composto por letras e/ou números'
): Rule => ({
    pattern: /\S+\s*\S*/,
    message,
    transform: trim,
});

const fullName = (message = 'Por favor, digite seu nome completo'): Rule => ({
    pattern: /\S+\s+\S+/,
    message,
    transform: trim,
});

const cardNumber = (message = 'O cartão inserido é inválido'): Rule => ({
    validator: validators.cardNumber(message),
    transform: _.flow(trim, extractNumber),
});

const cvv = (message = 'O código CVV é composto de três dígitos'): Rule => ({
    pattern: /[[0-9]{3}/,
    transform: trim,
    message,
});

const expirationDateFormat = (
    message = 'Por favor, digite a data no padrão mm/aaaa'
): Rule => ({
    pattern: /[0-9]{2}\/[0-9]{4}/,
    transform: trim,
    message,
});

const expirationDateValidity = (
    message = 'A data fornecida é inválida'
): Rule => ({
    validator: validators.cardExpirationDate(message),
    transform: trim,
});

const birthDate = (
    message = 'Por favor, insira a data no padrão dd/mm/aaaa. A data deve ser menor que a data atual.'
): Rule => ({
    validator: validators.birthDate(message),
    transform: trim,
});

const afterToday = (
    message = 'A data deve ser maior que a data atual.'
): Rule => ({
    validator: validators.after()(message),
    transform: trim,
});

const after = (
    after: DateTime,
    message = 'A data deve ser maior que a data indicada.'
): Rule => ({
    validator: validators.after(after)(message),
    transform: trim,
});

const todayOrAfter = (
    message = 'A data deve ser maior ou igual a data atual.'
): Rule => ({
    validator: validators.after(DateTime.now().minus({ days: 1 }))(message),
    transform: trim,
});

const before = (
    before: DateTime,
    message = 'A data deve ser menor que a data indicada.'
): Rule => ({
    validator: validators.before(before)(message),
    transform: trim,
});

const between = (
    after: DateTime,
    before: DateTime,
    message = 'A data deve estar entre as datas indicadas'
): Rule => ({
    validator: validators.between(after, before)(message),
    transform: trim,
});

const date = (
    message = 'Por favor, insira a data no padrão dd/mm/aaaa'
): Rule => ({
    validator: validators.date(message),
    transform: trim,
});

const dateTime = (
    message = 'Por favor, insira a data e hora no padrão dd/mm/aaaa HH:mm'
): Rule => ({
    validator: validators.dateTime(message),
    transform: trim,
});

const pastDateTime = (
    message = 'Por favor, insira uma data e hora menor que a data e hora atual'
): Rule => ({
    validator: validators.pastDateTime(message),
    transform: trim,
});

const futureDateTime = (
    message = 'Por favor, insira uma data e hora maior que a data e hora atual'
): Rule => ({
    validator: validators.futureDateTime(message),
    transform: trim,
});

const futureDate = (
    message = 'A data deve ser maior que a data atual.'
): Rule => ({
    validator: validators.futureDate(message),
    transform: trim,
});

const cpf = (message = 'O CPF informado é inválido'): Rule => ({
    validator: validators.cpf(message),
    transform: _.flow(trim, extractNumber),
});

const cnpj = (message = 'O CNPJ informado é inválido'): Rule => ({
    validator: validators.cnpj(message),
    transform: _.flow(trim, extractNumber),
});

const cpfOrCnpj = ({
    message = 'O documento informado é inválido',
    allowEmpty = false,
}: {
    message?: string;
    allowEmpty?: boolean;
}): Rule => ({
    validator: (obj, input) => {
        if (!input && allowEmpty) return Promise.resolve();
        return input.length > 11
            ? validators.cnpj(message)(obj, input)
            : validators.cpf(message)(obj, input);
    },
    transform: _.flow(trim, extractNumber),
});

const zipcode = (message = 'O CEP informado é inválido'): Rule => ({
    validator: validators.cep(message),
    transform: _.flow(trim, extractNumber),
});

const cellphone = (
    message = 'O Número de telefone informado é inválido'
): Rule => ({
    pattern: CELLPHONE_REGEX,
    validator: validators.cellphone(message),
    message,
    transform: extractNumber,
});

const phoneCellphone = (
    message = 'O Número de telefone informado é inválido'
): Rule => ({
    pattern: PHONE_CELLPHONE_REGEX,
    validator: validators.phoneCellphone(message),
    message,
    transform: extractNumber,
});

const optionalPhoneCellphone = (
    message = 'O número de telefone informado é inválido'
): Rule => ({
    pattern: PHONE_CELLPHONE_REGEX,
    validator: (object, input) => {
        if (input) {
            return validators.phoneCellphone(message)(object, input);
        }
        return Promise.resolve();
    },
    message,
    transform: extractNumber,
});

const numbersOnly = (message = 'Por favor, digite apenas números'): Rule => ({
    pattern: /^\d+$/,
    message,
});

const numbersAndSignOnly = (
    message = 'Por favor, digite apenas números'
): Rule => ({
    pattern: /^-?\d+$/,
    message,
});

const step = (step: number, message: string): Rule => ({
    validator: validators.step(step)(message),
});

const password = (): Rule[] => [
    {
        min: 8,
        max: 200,
        message: 'Deve ter de 8 a 200 caracteres.',
    },
    {
        pattern: /^[^\s]+$/g,
        message: 'Não deve conter espaços em branco.',
    },
    {
        pattern: /[A-Z]/g,
        message: 'Deve conter pelo menos 1 caractere em maiúsculo.',
    },
    {
        pattern: /[a-z]/g,
        message: 'Deve conter pelo menos 1 caractere em minúsculo.',
    },
    {
        pattern: /[0-9]/g,
        message: 'Deve conter pelo menos 1 dígito numérico.',
    },
];

const passwordConfirmation = (passwordFieldName = 'password'): Rule[] => [
    ({ getFieldValue }) => ({
        validator(rule, value) {
            return !value || getFieldValue(passwordFieldName) === value
                ? Promise.resolve()
                : Promise.reject(
                      Error('Senha e confirmação de senha não coincidem.')
                  );
        },
    }),
];

const accessCode = (
    message = 'Por favor, digite o código de acesso'
): Rule => ({
    pattern: /[A-Z0-9]{8}/,
    message,
    transform: trim,
});

const resourceVerificationCode = (
    length = 6,
    message = 'Por favor, digite o código de acesso'
): Rule => ({
    pattern: new RegExp('^[0-9]{' + length + '}$'),
    message,
    transform: trim,
});

const resourceVerificationCodeArray = (
    length = 6,
    message = 'Por favor, digite o código de acesso'
): Rule => ({
    validator(rule, value) {
        return value?.length === length
            ? Promise.resolve()
            : Promise.reject(Error(message));
    },
});

const characterLimit = (
    max: number,
    message = 'Limite de caracteres ultrapassado'
): Rule => ({
    max,
    message,
    transform: trim,
});

const maxValue = (
    max: number,
    message = 'Valor maior do que o permitido'
): Rule => ({
    validator: validators.max(max)(message),
    message,
});

const minValue = (
    min: number,
    message = 'Valor menor do que o permitido'
): Rule => ({
    validator: validators.min(min)(message),
    message,
});

const notEqualTo = (num: number, message = 'Valor não permitido'): Rule => ({
    validator: validators.notEqualTo(num)(message),
    message,
});

const uuid = (message = 'A chave não está no formato correto'): Rule => ({
    pattern: UUID_REGEX,
    message,
    transform: trim,
});

const addressField = (message = 'Campo possui caractere inválido'): Rule => ({
    pattern: ADDRESS_REGEX,
    message,
    transform: trim,
});

const url = (message = 'URL inválida'): Rule => ({
    validator: validators.url(message),
    message,
    transform: trim,
});

export default {
    accessCode,
    addressField,
    after,
    afterToday,
    before,
    between,
    birthDate,
    cardNumber,
    cellphone,
    characterLimit,
    cnpj,
    cpf,
    cpfOrCnpj,
    cvv,
    date,
    dateTime,
    email,
    expirationDateFormat,
    expirationDateValidity,
    fullName,
    futureDate,
    futureDateTime,
    maxValue,
    minValue,
    nickname,
    notEqualTo,
    numbersAndSignOnly,
    numbersOnly,
    optionalPhoneCellphone,
    password,
    passwordConfirmation,
    pastDateTime,
    phoneCellphone,
    required,
    resourceVerificationCode,
    resourceVerificationCodeArray,
    step,
    todayOrAfter,
    url,
    uuid,
    zipcode,
};
export { extractNumber, definedRules, sequentialRules };
